// REPANCY-AGENT-LOCK: agent-ux phase-K.9.8 (until 2026-05-25)
/**
 * K.9.8 — Preview pane for a connected-site FOLDER (not file).
 *
 * Renders when DocumentPreview's `previewSource.kind === 'remote-folder'`.
 * Triggered by clicking a folder ROW (the row body, not the ▶ expand
 * toggle) inside ConnectedFolderChildren in TreePane.
 *
 * Today the surface is intentionally minimal: name, "Open in
 * SharePoint" link, and child counts. Child counts come from the
 * same `listChildrenForSelectedSite` action the tree already uses;
 * cache hits are common because the user typically expanded the
 * folder before clicking its row.
 *
 * Deferred surfaces (not in K.9.8):
 *   - Inline file list with select-to-preview (today the file row in
 *     the tree is the canonical way to preview a file).
 *   - Folder-level metadata (createdAt, modifiedAt) — Graph returns
 *     this on the driveItem GET but listChildrenForSelectedSite
 *     doesn't surface it. Add when product asks.
 */

import { useEffect, useState, type JSX } from 'react';
import { useAction } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { testIds } from '../../lib/testIds';

interface RemoteFolderPreviewProps {
  siteId: string;
  driveId: string;
  folderId: string;
  name: string;
  webUrl: string | null;
}

const FONT = 'var(--xp-font-body)';

export function RemoteFolderPreview({
  siteId,
  driveId,
  folderId,
  name,
  webUrl,
}: RemoteFolderPreviewProps): JSX.Element {
  const listChildren = useAction(api.userSelectedSites.listChildrenForSelectedSite);
  const [counts, setCounts] = useState<{ folders: number; files: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setCounts(null);
    listChildren({ siteId, driveId, folderId })
      .then((result) => {
        if (cancelled) return;
        if (result.ok === false) {
          setError(reasonMessage(result.reason));
          return;
        }
        if (result.kind === 'children') {
          let folders = 0;
          let files = 0;
          for (const c of result.children) {
            if (c.kind === 'folder') folders += 1;
            else files += 1;
          }
          setCounts({ folders, files });
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [siteId, driveId, folderId, listChildren]);

  return (
    <div
      data-testid={testIds.explorer.remoteFolderRoot}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: '#f0f0f0',
        borderStyle: 'solid',
        borderWidth: 2,
        fontFamily: FONT,
        fontSize: 12,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 8px',
          background: '#ece9d8',
          borderBottom: '1px solid #808080',
          flexShrink: 0,
          gap: 8,
        }}
      >
        <span
          data-testid={testIds.explorer.remoteFolderTitle}
          style={{ fontWeight: 'bold', fontSize: 13 }}
        >
          📁 {name}
        </span>
        {webUrl !== null ? (
          <a
            data-testid={testIds.explorer.remoteFolderOpenInSharePoint}
            href={webUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 11 }}
          >
            Open in SharePoint ↗
          </a>
        ) : null}
      </div>
      <div style={{ padding: 12, flex: 1, overflow: 'auto' }}>
        {loading ? (
          <div
            data-testid={testIds.explorer.remoteFolderLoading}
            style={{ color: '#5a3e00' }}
          >
            Loading folder contents…
          </div>
        ) : error !== null ? (
          <div
            data-testid={testIds.explorer.remoteFolderError}
            style={{ color: '#a00' }}
          >
            {error}
          </div>
        ) : counts !== null ? (
          <div
            data-testid={testIds.explorer.remoteFolderChildCount}
            style={{ color: '#000', fontSize: 12 }}
          >
            <p style={{ margin: 0, marginBottom: 4 }}>
              <strong>{counts.folders}</strong> sub-folder
              {counts.folders === 1 ? '' : 's'}
            </p>
            <p style={{ margin: 0 }}>
              <strong>{counts.files}</strong> file
              {counts.files === 1 ? '' : 's'}
            </p>
            <p style={{ margin: 0, marginTop: 12, fontSize: 11, color: '#000' }}>
              Click a file in the tree to preview its contents.
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function reasonMessage(reason: string): string {
  switch (reason) {
    case 'not-authenticated':
      return 'Sign in to load this folder.';
    case 'site-not-granted':
      return 'Access to this site was revoked.';
    case 'no-app-only-token':
      return 'Server-side Microsoft token unavailable.';
    case 'graph-error':
    default:
      return 'Could not load folder contents.';
  }
}
